// ✅ P1-4: Throttle utility for performance optimization

/**
 * Throttles a function call, ensuring it's executed at most once per specified interval.
 * Useful for limiting the frequency of expensive operations like resize handlers.
 *
 * @template T - Function type to throttle
 * @param func - The function to throttle
 * @param limit - Minimum time in milliseconds between function executions
 * @returns Throttled version of the function
 *
 * @example
 * const throttledResize = throttle(() => {
 *   calculateLayout();
 * }, 200); // Execute at most once per 200ms
 *
 * window.addEventListener('resize', throttledResize);
 */
export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function throttled(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}
